import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { SignUp } from '@/features/ui';
import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';

import { useFormDraftPlainTools } from '@/hooks/index';
import { useMutation } from '@tanstack/react-query';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserService } from '@/api/types';

import api from '@/api';

import { SERVICE_CODE, STEP_NUMBER_SIGN_UP } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { personalDataBlockSchema, residenceAddressSchema, residenceDescriptionOfRequestSchema } from './model/schemas';
import { CitizenAppealMergedSchema } from './model/types';
import { PersonalData, DescriptionOfRequest, DataPreview, ServiceResult, ResidenceAddress } from './ui/steps';

import './CitizenAppealToMvs.scss';

export const STEPS: Array<Step<any>> = [
  {
    title: 'Особисті дані',
    step: PersonalData,
    validationSchema: personalDataBlockSchema,
  },
  { title: 'Дані про проживання', step: ResidenceAddress, validationSchema: residenceAddressSchema },
  {
    title: 'Опис звернення',
    step: DescriptionOfRequest,
    validationSchema: residenceDescriptionOfRequestSchema,
  },
  { title: 'Підтвердження даних', step: DataPreview, validationSchema: confirmationOfApplicationSchema },
  { title: 'Підписання документу КЕП', step: SignUp },
  { title: 'Результат', step: ServiceResult },
];

export const CitizenAppealToMvs: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);

  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);

  const { mutateAsync: generatePDF, isPending: isLoadingPdf } = useMutation({
    mutationFn: async ({
      code,
      payload,
    }: {
      code: Parameters<typeof api.services.generatePdf>[0];
      payload: Parameters<typeof api.services.generatePdf>[1];
    }) => {
      const result = await api.services.generatePdf(code, payload);
      serviceFileManager.setAttachment(result);
      return result;
    },
  });

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});

  const {
    isLoading: isLoadingDraft,
    currentRequestId,
    onDraftCreate,
    onDraftSave,
    onDraftContinue,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => {
      setDefaultValues(data ?? {});
    },
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
  });

  const handleCreateDraft = useCallback(
    async (data?: { stateForm: FormDataType }, specificStep?: (step: number) => void) => {
      return (
        specificStep &&
        onDraftCreate(
          {
            id: currentService.id,
            name: currentService.name,
            code: SERVICE_CODE,
            totalSteps: STEPS.length,
            currentStep: 1,
            lastStepFilled: 0,
          },
          specificStep,
          data,
        )
      );
    },
    [onDraftCreate, currentService.id, currentService.name, currentService.code, STEPS.length],
  );

  const handleGeneratePDF = useCallback(
    async (formData: CitizenAppealMergedSchema): Promise<void> => {
      const { commonPayload } = generatePayload({
        requestId: currentRequestId ?? '',
        service: currentService,
        formData,
      });

      await generatePDF({ code: SERVICE_CODE, payload: commonPayload }).catch((error) => console.error(error));
    },
    [currentRequestId, currentService, generatePDF],
  );

  const handleSubmitForm = useCallback(
    async (formData: CitizenAppealMergedSchema): Promise<void> => {
      const { signedPayload } = generatePayload({
        requestId: currentRequestId ?? '',
        service: currentService,
        formData,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      }).catch((error) => console.error(error));

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm],
  );

  const isLoading = isLoadingPdf || isLoadingDraft || isLoadingSubmit;

  return (
    <ServiceFormTemplate
      className="citizen-appeal-to-mvs-service"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleCreateDraft}
      stepProps={{
        handleGeneratePDF,
        currentService,
        serviceFileManager,
        submitForm: handleSubmitForm,
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
      }}
    />
  );
};
